import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { getAddressFromCoords } from "../../api/geocoding";
import { GetPastHistory } from "../../types/types";
import useGetTripDistance from "../../hooks/useGetTripDistance";
import { DotLoader } from "../../components/dotLoader";
import { formatDate, textSubstring, timeCalculator } from "../../utils/util";
import theme from "../../theme";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { deviceImageArray } from "../../utils/deviceImageArray";
import { useI18n } from "../../locales";
import { getEventTypeKey, getEventTypeValue } from "../../utils/langUtils";

const { sm, md } = theme.screen;

const InfoContent: React.FC<{
  data: GetPastHistory;
}> = (props) => {
  const { t } = useI18n();
  const {
    data: el,
  } = props;
  const [road, setRoad] = useState("");
  const [roadLoading, setroadLoading] = useState(false);

  const { data: tripDistanceData, isLoading: tripDistanceDataIsLoading } =
    useGetTripDistance({
      mobility_seq: el?.seq,
      seq: Number(el?.running_seq),
    });

  // 이벤트명을 언어팩에 맞게 변환 (배터리 전압 등 추가 정보는 유지)
  const getLocalizedEventLabel = (rawEvent: string) => {
    const eventKey = getEventTypeKey(rawEvent, t);
    if (eventKey && eventKey !== "normal") {
      const baseLabel = getEventTypeValue(eventKey, t);
      const voltageMatch = rawEvent.match(/\([^)]*\)/);
      const bracketMatch = rawEvent.match(/\[[^\]]*\]/);
      const additionalInfo = voltageMatch
        ? voltageMatch[0]
        : bracketMatch
        ? bracketMatch[0]
        : "";
      return additionalInfo ? `${baseLabel}${additionalInfo}` : baseLabel;
    }

    return rawEvent;
  };

  const getRoad = async () => {
    setroadLoading(true);
    try {
      const address = await getAddressFromCoords(el.latitude, el.longitude);
      setRoad(address);
    } catch (error) {
      console.error("주소 조회 오류:", error);
      setRoad("현재 위치를 가져올 수 없습니다.");
    } finally {
      setroadLoading(false);
    }
  };

  useEffect(() => {
    if (el) {
      getRoad();
    }
  }, [el]);

  // const handleClick = () => {
  //   // 닫기 버튼
  //   setRoad("");
  // };

  // console.log(el);
  return (
    <Wrapper>
      <Header>
        <CustomTooltip
          title={el?.mobility_name}
          active={el?.mobility_name?.length > 12}
        >
          <Mobility>
            {window.innerWidth > 450 && el.mobility_name
              ? el.mobility_name != null
                ? el.mobility_name.length > 12
                  ? el.mobility_name.substring(
                      0,
                      12 - el.mobility_type.length
                    ) + "..."
                  : el.mobility_name
                : ""
              : el.mobility_name}
          </Mobility>
        </CustomTooltip>

        <Id>{`[${t.history.deviceTypeLabel}: ${el.mobility_type}]`}</Id>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <StatusCircle
            bgColor={
              el.running_status !== "운행중"
                ? theme.color.neutral_400
                : theme.color.green_500
            }
          />
          <StatusText>{el.running_status}</StatusText>
        </div>
      </Header>

      <FirstRow>
        {el?.mobility_image === null ? (
          <CustomImg
            src={`${process.env.REACT_APP_BASE_URL}/img/${
              deviceImageArray.find(
                (list) => list.device === el.mobility_type
              )?.popUpFileName
            }`}
          />
        ) : (
          <CustomImg
            src={`${process.env.REACT_APP_BASE_URL}/${el?.mobility_image}`}
          />
        )}

        <RightWrapper>
          <TextWrapper>
            <ThinText>{t.history.lastOperation}</ThinText>
            <TimeContainer>
              <ThickText>
                {el.max_registered_at !== null
                  ? formatDate(el.max_registered_at, "yyyy-MM-dd HH:mm")
                  : t.mobileList.noRecord}
              </ThickText>
              {el.max_registered_at && (
                <ThickText>
                  {timeCalculator(
                    new Date(el.min_registered_at),
                    new Date(el.max_registered_at),
                    ` ${t.history.operating}`
                  )}
                </ThickText>
              )}
            </TimeContainer>
          </TextWrapper>
          <TextWrapper>
            <ThinText>{t.history.distance}</ThinText>

            <ThickText>
              {tripDistanceDataIsLoading && <DotLoader />}

              {tripDistanceData && (
                <span>{tripDistanceData?.trip_distance + " km"}</span>
              )}
            </ThickText>
          </TextWrapper>
          <TextWrapper>
            <ThinText>{t.history.machineryVoltage}</ThinText>
            <ThickText>
              {el.board_input_volt ? el.board_input_volt + "V" : "0.0V"}
            </ThickText>
          </TextWrapper>
          <StatusWrapper>
            <ThinText>{t.history.currentStatus}</ThinText>
            <>
              {el.events === null ? (
                <Clear>{t.history.normal}</Clear>
              ) : (
                el.events.map((elel, idx) => {
                  const eventKey = getEventTypeKey(elel, t);
                  const isRollover =
                    eventKey === "vehicleRolloverEvent" || elel === "차량전복";
                  const label = getLocalizedEventLabel(elel);

                  return isRollover ? (
                    <Danger key={idx.toString()}>{label}</Danger>
                  ) : (
                    <DangerZone key={idx.toString()}>{label}</DangerZone>
                  );
                })
              )}
            </>
          </StatusWrapper>
        </RightWrapper>
      </FirstRow>
      <SecondRow>
        <SRLeft>
          <TextWrapper2>
            <SubText>{t.history.detectionDetails2}</SubText>
            {(() => {
              const codeKey = getEventTypeKey(el.code_name, t);
              const isRollover =
                codeKey === "vehicleRolloverEvent" ||
                el.code_name === "차량전복";
              const label = getLocalizedEventLabel(el.code_name);

              return isRollover ? (
                <Danger
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {label}
                </Danger>
              ) : (
                <DangerZone
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {label}
                </DangerZone>
              );
            })()}
          </TextWrapper2>
          <TextWrapper2>
            <SubText>{t.history.detectionDateTime}</SubText>
            <MainText>
              <span>{formatDate(el.registered_at, `yyyy-MM-dd HH:mm`)}</span>
            </MainText>
          </TextWrapper2>
          <TextWrapper2>
            <SubText>{t.history.detectionLocation}</SubText>
            <MainText
              style={{
                display: "inline-block",
                maxWidth: "200px",
                whiteSpace: "normal",
              }}
            >
              {roadLoading && <DotLoader />}
              <span>{road}</span>
            </MainText>
          </TextWrapper2>
          {el.clear_reason && (
            <TextWrapper2>
              <SubText>{t.history.releaseReason2}</SubText>
              <CustomTooltip
                title={el?.clear_reason}
                active={el?.clear_reason?.length > 12}
              >
                <MainText>
                  {textSubstring(el.clear_reason, 0, 12, theme.screen.sm)}
                </MainText>
              </CustomTooltip>

              <MainSmText>
                {formatDate(el.clear_at as string, "[yyyy-MM-dd HH:mm]")}
              </MainSmText>
            </TextWrapper2>
          )}
        </SRLeft>
        <SRRight>
          <CustomTooltip title={el?.name} active={el?.name?.length > 13}>
            <TextWrapper2>
              <SubText>{t.history.user2}</SubText>
              <MainText>
                {textSubstring(el.name, 0, 13, theme.screen.sm)}
              </MainText>
            </TextWrapper2>
          </CustomTooltip>

          <TextWrapper2>
              <SubText>{t.history.userContact}</SubText>
            <MainText>
              {textSubstring(el.phone, 0, 13, theme.screen.sm)}
            </MainText>
          </TextWrapper2>

          <CustomTooltip
            title={el?.nok_name}
            active={el?.nok_name?.length > 13}
          >
            <TextWrapper2>
              <SubText>{t.history.guardian}</SubText>
              <MainText>
                {textSubstring(el.nok_name, 0, 13, theme.screen.sm)}
              </MainText>
            </TextWrapper2>
          </CustomTooltip>

          <TextWrapper2>
            <SubText>{t.history.guardianContact}</SubText>
            <MainText>
              {textSubstring(el.nok_phone, 0, 13, theme.screen.sm)}
            </MainText>
          </TextWrapper2>

          <CustomTooltip
            title={el?.relation}
            active={el?.relation?.length > 13}
          >
            <TextWrapper2>
              <SubText>{t.history.guardianUserRelation}</SubText>
              <MainText>
                {textSubstring(el.relation, 0, 13, theme.screen.sm)}
              </MainText>
            </TextWrapper2>
          </CustomTooltip>
        </SRRight>
      </SecondRow>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 513px;
  padding-left: 25px;
  padding-right: 25px;
  background: ${theme.color.white};
  cursor: default;
  position: relative;

  @media (max-width: ${sm}px) {
    width: 90vw;
    padding: 25px 20px;
    min-width: 320px;
  }

  @media (max-width: ${md}px) {
    width: 90vw;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;

  @media (max-width: ${sm}px) {
    flex-wrap: wrap;
    margin-bottom: 15px;
    gap: 5px;
  }
`;

const Mobility = styled.p`
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;
  color: ${theme.color.neutral_600};
  margin-right: 5px;

  @media (max-width: ${sm}px) {
    font-size: 14px;
  }
`;

const Id = styled.strong`
  color: ${theme.color.neutral_500};
  font-weight: 700;
  font-size: "18px";
  line-height: "21px";
  @media (max-width: ${sm}px) {
    font-size: 12px;
    margin-right: 8px;
  }
`;

const FirstRow = styled.div`
  display: flex;
  gap: 25px;
  @media (max-width: ${sm}px) {
    gap: 15px;
  }
`;

const CustomImg = styled.img`
  width: 117px;
  height: 117px;
  border-radius: 50%;

  @media (max-width: ${sm}px) {
    width: 70px;
    height: 70px;
  }
`;

export const SecondRow = styled.div`
  width: 463px;
  display: flex;
  background: ${theme.color.neutral_50};
  border-radius: 20px;
  margin-top: 40px;
  padding: 20px;
  margin-bottom: 40px;

  @media (max-width: ${sm}px) {
    width: 100%;
    height: auto;
    /* min-height: 300px; */
    padding: 15px;
    margin-top: 10px;
    margin-bottom: 10px;
  }
  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    width: 100%;
    padding: 15px;
  }
`;

export const RightWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ThinText = styled.span`
  font-weight: 400;
  margin-right: 10px;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.color.neutral_500};
  min-width: 75px;

  @media (max-width: ${sm}px) {
    font-size: 13px;
    margin-right: 5px;
    min-width: 65px;
  }

  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    font-size: 13px;
    min-width: 65px;
  }
`;

export const SRLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  border-right: 1px solid ${theme.color.neutral_200};
  width: 50%;

  @media (max-width: ${sm}px) {
    gap: 5px;
    padding-right: 10px;
  }
`;

export const SRRight = styled.div`
  display: flex;
  padding-left: 20px;
  flex-direction: column;
  gap: 25px;
  width: 50%;

  @media (max-width: ${sm}px) {
    gap: 5px;
    padding-left: 10px;
  }
`;

export const TextWrapper2 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const SubText = styled.span`
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.color.neutral_500};
`;

export const MainText = styled.span`
  min-height: 17px;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.color.neutral_600};
  display: flex;
  gap: 3px;
  flex-wrap: wrap;

  @media (max-width: ${sm}px) {
    min-height: 17px;
    font-size: 10px;
    white-space: normal;
  }
  @media (max-width: ${md}px) {
    font-size: 12px;
  }
`;

const MainSmText = styled.span`
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.color.neutral_600};

  @media (max-width: ${sm}px) {
    font-size: 9px;
  }
  @media (max-width: ${md}px) {
    font-size: 11px;
  }
`;

export const ThickText = styled.div`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  min-height: 18px;
  color: ${theme.color.neutral_500};

  @media (max-width: ${sm}px) {
    font-size: 11px;
  }

  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    font-size: 13px;
  }

  @media (min-width: ${sm + 1}px) {
    margin-bottom: 3px;
  }
`;

export const TextWrapper = styled.div`
  margin-bottom: 15px;
  display: flex;

  @media (max-width: ${sm}px) {
    display: flex;
    margin-bottom: 5px;
  }
`;

const StatusWrapper = styled.div`
  display: flex;
  gap: 3px;
  @media (max-width: ${sm}px) {
    flex-wrap: wrap;
  }
`;

const Clear = styled.div`
  background: ${theme.color.green_500};
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.white};
  height: 19px;
  padding: 0px 6px;
  border-radius: 5px;
  display: flex;
  align-items: center;

  @media (max-width: ${sm}px) {
    width: 70px;
    justify-content: center;
    font-size: 11px;
    height: 18px;
  }
`;

const DangerZone = styled.div`
  padding: 2px 6px;
  border-radius: 5px;
  border: 0.5px solid ${theme.color.orange_500};
  background: ${theme.color.orange_100};
  color: ${theme.color.orange_500};
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  display: flex;
  align-items: center;
  width: fit-content;

  @media (max-width: ${sm}px) {
    width: 70px;
    justify-content: center;
    font-size: 11px;
    height: 18px;
  }
`;

const Danger = styled.div`
  padding: 2px 6px;
  border-radius: 5px;
  color: ${theme.color.red_100};
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  background: ${theme.color.red_500};
  display: flex;
  align-items: center;
  width: fit-content;

  @media (max-width: ${sm}px) {
    width: 70px;
    justify-content: center;
    font-size: 11px;
    height: 18px;
  }
`;

// const BtnWrapper = styled.div`
//   width: 100%;
//   display: flex;
//   gap: 25px;
//   @media (max-width: ${sm}px) {
//     margin-bottom: 10px;
//   }
// `;

const StatusCircle = styled.div<{ bgColor: string }>`
  width: 7px;
  height: 7px;
  background-color: ${({ bgColor }) => bgColor};
  border-radius: 50%;
  margin-left: 8px;
  margin-right: 8px;
  @media (max-width: ${sm}px) {
    margin-left: 0px;
  }
`;

const StatusText = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: ${theme.color.neutral_600};

  @media (max-width: ${sm}px) {
    font-size: 12px;
  }
`;

// const ReportBtn = styled.button`
//   width: 219px;
//   height: 50px;
//   background: ${theme.color.white};
//   box-shadow: 0px 1px 2px 1px rgba(0, 0, 0, 0.25);
//   border-radius: 10px;
//   font-weight: 700;
//   font-size: 15px;
//   line-height: 24px;
//   color: ${theme.color.neutral_500};
//   display: flex;
//   flex: 1;
//   align-items: center;
//   justify-content: center;
//   border: none;
//   cursor: pointer;
//   &:hover {
//     box-shadow: 0px 1px 7px 1px rgba(0, 0, 0, 0.25);
//   }
//   @media (max-width: ${sm}px) {
//     width: 80%;
//     height: 35px;
//     font-size: 13px;
//   }
// `;

// const FakeDiv = styled.div`
//   width: 70px;
//   height: 70px;
//   background: ${theme.color.white};
//   position: absolute;
//   left: 50%;
//   transform: rotateZ(45deg) translateX(-50px);
//   bottom: -70px;
//   z-index: 3;

//   @media (max-width: ${sm}px) {
//     z-index: -1;
//     left: 53%;
//     width: 40px;
//     height: 40px;
//     transform: rotateZ(45deg) translateX(-42px);
//     bottom: -50px;
//   }
// `;

// const XButtonContainer = styled.div`
//   position: absolute;
//   top: 10px;
//   right: 10px;
//   color: ${theme.color.neutral_400};

//   @media (max-width: ${sm}px) {
//     font-size: small;
//     top: 5px;
//     right: 5px;
//   }
// `;

const TimeContainer = styled.div`
  @media (max-width: ${sm}px) {
  }
`;

// const BubbleContainer = styled.div`
//   position: absolute;
//   bottom: -34px;
//   right: 50%;
//   transform: translateX(50%);
//   z-index: 10;

//   img {
//     width: 50px;
//   }
// `;

export default InfoContent;
